import { Landmark, Mail, Phone, Instagram, MessageCircle, Accessibility, Car, CreditCard, CalendarCheck, Building2 } from 'lucide-react';

import type { SiteContent } from '../../content/types';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

interface ContactCardsProps {
  content: SiteContent;
}

export function ContactCards({ content }: ContactCardsProps): JSX.Element {
  const featureGroups = [
    { items: content.contact.accessibility, icon: Accessibility },
    { items: content.contact.parking, icon: Car },
    { items: content.contact.payments, icon: CreditCard },
    { items: content.contact.planning, icon: CalendarCheck },
    { items: content.contact.facilities, icon: Building2 },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {content.contactPage.infoCards.map((card) => {
          const title = card.title.toLowerCase();
          const Icon = title.includes('telefon')
            ? Phone
            : title.includes('mail')
              ? Mail
              : title.includes('instagram')
                ? Instagram
                : title.includes('whatsapp')
                  ? MessageCircle
                  : Landmark;

          const map = new Map<string, string>([
            [content.contact.phoneDisplay, content.contact.phoneHref],
            [content.contact.emailDisplay, content.contact.emailHref],
            [`${content.contact.addressLine1}, ${content.contact.postalCode} ${content.contact.city}`, content.contact.googleMapsUrl],
            [`@${content.contact.instagramUrl.split('/').filter(Boolean).pop() ?? ''}`, content.contact.instagramUrl],
            [content.contact.whatsappDisplay, content.contact.whatsappHref],
          ]);
          const href = map.get(card.text);

          return (
            <Card key={card.title} className="bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="h-4 w-4 text-[color:var(--color-primary)]" />
                  {card.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {href ? (
                  <a href={href} className="text-sm text-[color:var(--color-muted)] hover:text-[color:var(--color-primary)]" target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noreferrer' : undefined}>
                    {card.text}
                  </a>
                ) : (
                  <p className="text-sm text-[color:var(--color-muted)]">{card.text}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{content.contact.insuranceInfo.headline}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[color:var(--color-muted)]">{content.contact.insuranceInfo.text}</p>
          <div className="flex flex-wrap gap-2">
            {featureGroups.flatMap((group, groupIndex) =>
              group.items.map((item) => {
                const Icon = group.icon;
                return (
                  <Badge key={`${item}-${groupIndex}`} variant="outline" className="gap-1.5">
                    <Icon className="h-3.5 w-3.5" />
                    {item}
                  </Badge>
                );
              }),
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
